import { useState } from 'react';

import { IconButton, TextField } from '../../ui/controls.js';
import type { SettingsSectionProps } from '../settings-types.js';
import { DEFAULT_LAYER, isObj, layerList, MAX_LAYERS, renameLayerInPairs, uniqueName } from '../settings-validate.js';

/**
 * Unity's Tags and Layers: the ordered layer list (`settings.layers`, layer
 * i = bit i in masks, at most 32, "Default" fixed first) and the project tag
 * list (`settings.tags`). Renaming or removing a layer updates the collision
 * matrix (`settings.physics.ignoreCollisions`) in the same undo step.
 */
export function TagsLayers({ settings, update }: SettingsSectionProps) {
  const layers = layerList(settings);
  const tags = Array.isArray(settings.tags) ? settings.tags.filter((t): t is string => typeof t === 'string') : [];
  const [error, setError] = useState<string | null>(null);
  // Bumped when an edit is refused so the text fields show the stored names again.
  const [reset, setReset] = useState(0);
  const physics = isObj(settings.physics) ? settings.physics : undefined;

  const refuse = (message: string) => {
    setError(message);
    setReset((r) => r + 1);
  };

  const layersValue = (next: string[]) => (next.length === 1 && next[0] === DEFAULT_LAYER ? undefined : next);
  const physicsWith = (pairs: unknown[]) => {
    if (!physics && pairs.length === 0) return undefined;
    const next = { ...(physics ?? {}) };
    if (pairs.length > 0) next.ignoreCollisions = pairs;
    else delete next.ignoreCollisions;
    return Object.keys(next).length > 0 ? next : undefined;
  };

  const renameLayer = (index: number, raw: string) => {
    const name = raw.trim();
    const old = layers[index]!;
    if (name === old) return;
    if (!name) return refuse('Layer names cannot be empty.');
    if (layers.includes(name)) return refuse(`There already is a layer named "${name}".`);
    setError(null);
    const next = layers.map((l, i) => (i === index ? name : l));
    update(
      { layers: layersValue(next), physics: physicsWith(renameLayerInPairs(physics?.ignoreCollisions, old, name)) },
      `Rename layer "${old}" to "${name}"`,
    );
  };

  const removeLayer = (index: number) => {
    const old = layers[index]!;
    setError(null);
    update(
      { layers: layersValue(layers.filter((_, i) => i !== index)), physics: physicsWith(renameLayerInPairs(physics?.ignoreCollisions, old, null)) },
      `Remove layer "${old}"`,
    );
  };

  const moveLayer = (index: number, delta: number) => {
    const to = index + delta;
    const next = layers.slice();
    [next[index], next[to]] = [next[to]!, next[index]!];
    update({ layers: layersValue(next) }, `Move layer "${layers[index]}"`);
  };

  const addLayer = () => {
    setError(null);
    const name = uniqueName(layers, `Layer ${layers.length}`);
    update({ layers: [...layers, name] }, `Add layer "${name}"`);
  };

  const tagsValue = (next: string[]) => (next.length > 0 ? next : undefined);
  const renameTag = (index: number, raw: string) => {
    const name = raw.trim();
    const old = tags[index]!;
    if (name === old) return;
    if (!name) return refuse('Tag names cannot be empty.');
    if (tags.includes(name)) return refuse(`There already is a tag named "${name}".`);
    setError(null);
    update({ tags: tags.map((t, i) => (i === index ? name : t)) }, `Rename tag "${old}" to "${name}"`);
  };
  const addTag = (raw: string) => {
    const name = raw.trim();
    if (!name) return;
    if (tags.includes(name)) return refuse(`There already is a tag named "${name}".`);
    setError(null);
    update({ tags: [...tags, name] }, `Add tag "${name}"`);
  };

  // Layer 0 stays "Default" (entities without a layer component use it).
  const locked = (i: number) => i === 0 && layers[0] === DEFAULT_LAYER;

  return (
    <div className="tags-layers">
      <div className="tags-layers-head">
        <strong>Layers</strong>
        <span className="muted">
          {layers.length} / {MAX_LAYERS}
        </span>
      </div>
      <ol className="tags-layers-list">
        {layers.map((layer, i) => (
          <li key={`${i}:${layer}:${reset}`} className="tags-layers-row">
            <span className="tags-layers-index muted">{i}</span>
            {locked(i) ? (
              <span className="tags-layers-fixed" title="Built-in: entities without a layer component are on Default">
                {layer}
              </span>
            ) : (
              <TextField value={layer} onChange={(v) => renameLayer(i, v)} />
            )}
            <IconButton icon="arrowUp" title="Move up" size={13} disabled={i === 0 || locked(i - 1) || locked(i)} onClick={() => moveLayer(i, -1)} />
            <IconButton icon="arrowDown" title="Move down" size={13} disabled={i === layers.length - 1 || locked(i)} onClick={() => moveLayer(i, 1)} />
            <IconButton icon="trash" title="Remove layer" size={13} disabled={locked(i) || layers.length === 1} onClick={() => removeLayer(i)} />
          </li>
        ))}
      </ol>
      <button type="button" className="ghost small" disabled={layers.length >= MAX_LAYERS} onClick={addLayer}>
        + Add Layer
      </button>
      <p className="card-note muted">
        Layer i is bit i in culling and collision masks. Entities keep the layer name they store, so rename them too (prism check
        reports unknown layers).
      </p>

      <div className="tags-layers-head">
        <strong>Tags</strong>
        <span className="muted">{tags.length === 0 ? 'any tag allowed' : `${tags.length}`}</span>
      </div>
      <ol className="tags-layers-list">
        {tags.map((tag, i) => (
          <li key={`${i}:${tag}:${reset}`} className="tags-layers-row">
            <span className="tags-layers-index muted">{i}</span>
            <TextField value={tag} onChange={(v) => renameTag(i, v)} />
            <IconButton
              icon="trash"
              title="Remove tag"
              size={13}
              onClick={() => update({ tags: tagsValue(tags.filter((_, j) => j !== i)) }, `Remove tag "${tag}"`)}
            />
          </li>
        ))}
      </ol>
      <input
        key={`new-tag:${reset}:${tags.length}`}
        className="textfield tags-layers-new"
        placeholder="New tag… (Enter)"
        onKeyDown={(e) => {
          if (e.key === 'Enter') addTag(e.currentTarget.value);
        }}
      />
      <p className="card-note muted">With a tag list, prism check warns about entity tags that are not in it.</p>
      {error && <div className="settings-issue is-error">{error}</div>}
    </div>
  );
}
